import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaUsers,
  FaShoppingCart,
  FaDollarSign,
  FaClock,
  FaSync,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import SalesChart from "../../../components/SalesChart/SalesChart";
import CustomSelect from "../../../components/utils/CustomSelect";
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
  Button,
  DashboardGrid,
  DashboardCard,
  CardHeader,
  CardIcon,
  CardValue,
  CardLabel,
  DataTable,
  UserInfo,
  UserAvatar,
  StatusBadge,
  DataTableScroll,
  ActivitiesSection,
  ActivitiesTitle,
  TableHeader,
  TableRow,
  TableCell,
  LoadingCell,
  EmptyCell,
  CardChange,
} from "./AdminPanel.styles";
import { toast } from "sonner";
import {
  getDashboardStats,
  getRecentActivities,
} from "../../../store/slices/admin/asyncThunks";
import {
  selectStats,
  selectActivities,
  selectStatsLoading,
  selectActivitiesLoading,
  selectSelectedTimeframe,
  setTimeframe,
} from "../../../store/slices/admin/slice";
import { priceFormatter } from "../../../utils/utils";

const AdminPanel = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const stats = useSelector(selectStats);
  const activities = useSelector(selectActivities);
  const statsLoading = useSelector(selectStatsLoading);
  const activitiesLoading = useSelector(selectActivitiesLoading);
  const selectedTimeframe = useSelector(selectSelectedTimeframe);

  // Local state
  const [isLoading, setIsLoading] = useState(false);

  // Load data on component mount and when timeframe changes
  useEffect(() => {
    dispatch(getDashboardStats(selectedTimeframe));
    dispatch(getRecentActivities(10));
  }, [dispatch, selectedTimeframe]);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const promises = [
        dispatch(getDashboardStats(selectedTimeframe)).unwrap(),
        dispatch(getRecentActivities(10)).unwrap(),
      ];

      await Promise.all(promises);
      toast.success("Podaci su osveženi!");
    } catch (error) {
      toast.error("Greška pri osvežavanju podataka");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeframeChange = (newTimeframe) => {
    dispatch(setTimeframe(newTimeframe));
  };

  const timeframeOptions = [
    { value: "7days", label: "Poslednja 7 dana" },
    { value: "30days", label: "Poslednji mesec" },
    { value: "3months", label: "Poslednja 3 meseca" },
    { value: "1year", label: "Poslednja godina" },
    { value: "all", label: "Od početka rada" },
  ];

  return (
    <>
      <SectionHeader>
        <SectionTitle>Dashboard Pregled</SectionTitle>
        <SectionActions>
          <CustomSelect
            $small
            value={selectedTimeframe}
            onChange={handleTimeframeChange}
            options={timeframeOptions}
            ariaLabel="Izaberite period"
          />
          <Button
            $secondary
            onClick={refreshData}
            disabled={isLoading}
            $loading={isLoading}
          >
            <FaSync />
            Osveži
          </Button>
        </SectionActions>
      </SectionHeader>
      <DashboardGrid>
        <DashboardCard $type="revenue">
          <CardHeader>
            <CardIcon $type="revenue">
              <FaDollarSign />
            </CardIcon>
          </CardHeader>
          <CardValue>
            {statsLoading ? (
              <l-line-spinner
                size="24"
                stroke="2"
                speed="1"
                color="var(--accent)"
              />
            ) : (
              priceFormatter(stats?.revenue?.total || "0") + " RSD"
            )}
          </CardValue>
          <CardLabel>Ukupni Prihod</CardLabel>
          {!statsLoading && stats?.revenue && stats.revenue.change != null && (
            <CardChange $positive={stats.revenue.isPositive}>
              {stats.revenue.isPositive ? <FaArrowUp /> : <FaArrowDown />}
              {`${stats.revenue.isPositive ? "+" : ""}${stats.revenue.change}% od prošlog perioda`}
            </CardChange>
          )}
        </DashboardCard>

        <DashboardCard $type="users">
          <CardHeader>
            <CardIcon $type="users">
              <FaUsers />
            </CardIcon>
          </CardHeader>
          <CardValue>
            {statsLoading ? (
              <l-line-spinner
                size="24"
                stroke="2"
                speed="1"
                color="var(--accent)"
              />
            ) : (
              stats?.users?.total || "0"
            )}
          </CardValue>
          <CardLabel>Registrovani Korisnici</CardLabel>
          {!statsLoading && stats?.users && (
            <CardChange $positive>
              <FaArrowUp />
              {`+${stats.users.new || 0} novih u periodu`}
            </CardChange>
          )}
        </DashboardCard>

        <DashboardCard $type="orders">
          <CardHeader>
            <CardIcon $type="orders">
              <FaShoppingCart />
            </CardIcon>
          </CardHeader>
          <CardValue>
            {statsLoading ? (
              <l-line-spinner
                size="24"
                stroke="2"
                speed="1"
                color="var(--accent)"
              />
            ) : (
              stats?.orders?.total || "0"
            )}
          </CardValue>
          <CardLabel>Završene Prodaje</CardLabel>
          {!statsLoading && stats?.orders && stats.orders.change != null && (
            <CardChange $positive={stats.orders.isPositive}>
              {stats.orders.isPositive ? <FaArrowUp /> : <FaArrowDown />}
              {`${stats.orders.isPositive ? "+" : ""}${stats.orders.change}% od prošlog perioda`}
            </CardChange>
          )}
        </DashboardCard>
      </DashboardGrid>
      <SalesChart timeframe={selectedTimeframe} />
      <ActivitiesSection>
        <ActivitiesTitle>
          <FaClock />
          Nedavne Aktivnosti
        </ActivitiesTitle>
        <DataTableScroll>
          <DataTable>
            <TableHeader>Vreme</TableHeader>
            <TableHeader>Korisnik</TableHeader>
            <TableHeader>Akcija</TableHeader>
            <TableHeader>Detalji</TableHeader>
            {activitiesLoading ? (
              <TableRow>
                <LoadingCell>
                  <l-line-spinner
                    size="24"
                    stroke="3"
                    speed="1"
                    color="var(--accent)"
                  />
                </LoadingCell>
              </TableRow>
            ) : activities?.orders?.length ||
              activities?.completions?.length ||
              activities?.certificateApplications?.length ? (
              <>
                {activities?.orders?.slice(0, 5)?.map((order) => (
                  <TableRow key={`order-${order.id}`}>
                    <TableCell>
                      {new Date(order.createdAt).toLocaleDateString(
                        "sr-latn-RS",
                      )}
                    </TableCell>
                    <TableCell>
                      {order.user?.firstName?.trim() &&
                      order.user?.lastName?.trim() ? (
                        <UserInfo>
                          <UserAvatar>
                            {order.user?.firstName?.[0]}
                            {order.user?.lastName?.[0]}
                          </UserAvatar>
                          <span>
                            {order.user?.firstName} {order.user?.lastName}
                          </span>
                        </UserInfo>
                      ) : (
                        <UserInfo>
                          <UserAvatar>
                            {order.user?.email?.slice(0, 2)}
                          </UserAvatar>
                          <span>
                            {order.user?.email?.length > 40
                              ? order.user?.email.slice(0, 40) + "..."
                              : order?.user?.email}
                          </span>
                        </UserInfo>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge $type="active">Kupovina</StatusBadge>
                    </TableCell>
                    <TableCell>{priceFormatter(order.total)} RSD</TableCell>
                  </TableRow>
                ))}

                {activities?.completions?.slice(0, 3)?.map((completion) => (
                  <TableRow key={`completion-${completion.id}`}>
                    <TableCell>
                      {new Date(completion.completedAt).toLocaleDateString(
                        "sr-latn-RS",
                      )}
                    </TableCell>
                    <TableCell>
                      {completion.user?.firstName?.trim() &&
                      completion.user?.lastName?.trim() ? (
                        <UserInfo>
                          <UserAvatar>
                            {completion.user?.firstName?.[0]}
                            {completion.user?.lastName?.[0]}
                          </UserAvatar>
                          <span>
                            {completion.user?.firstName}{" "}
                            {completion.user?.lastName}
                          </span>
                        </UserInfo>
                      ) : (
                        <UserInfo>
                          <UserAvatar>
                            {completion.user?.email?.slice(0, 2)}
                          </UserAvatar>
                          <span>
                            {completion.user?.email?.length > 40
                              ? completion.user?.email.slice(0, 40) + "..."
                              : completion?.user?.email}
                          </span>
                        </UserInfo>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge $type="completed">Završio kurs</StatusBadge>
                    </TableCell>
                    <TableCell>{completion.course?.title}</TableCell>
                  </TableRow>
                ))}

                {activities?.certificateApplications
                  ?.slice(0, 3)
                  ?.map((app) => (
                    <TableRow key={`cert-${app.id}`}>
                      <TableCell>
                        {new Date(app.appliedAt).toLocaleDateString(
                          "sr-latn-RS",
                        )}
                      </TableCell>
                      <TableCell>
                        {app.user?.firstName?.trim() &&
                        app.user?.lastName?.trim() ? (
                          <UserInfo>
                            <UserAvatar>
                              {app.user?.firstName?.[0]}
                              {app.user?.lastName?.[0]}
                            </UserAvatar>
                            <span>
                              {app.user?.firstName} {app.user?.lastName}
                            </span>
                          </UserInfo>
                        ) : (
                          <UserInfo>
                            <UserAvatar>
                              {app.user?.email?.slice(0, 2)}
                            </UserAvatar>
                            <span>
                              {app.user?.email?.length > 40
                                ? app.user?.email.slice(0, 40) + "..."
                                : app?.user?.email}
                            </span>
                          </UserInfo>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          $type={
                            app.status === "approved" ? "completed" : "pending"
                          }
                        >
                          Aplicirao za sertifikat
                        </StatusBadge>
                      </TableCell>
                      <TableCell>{app.course?.title}</TableCell>
                    </TableRow>
                  ))}
              </>
            ) : (
              <EmptyCell>Nema dostupnih aktivnosti</EmptyCell>
            )}
          </DataTable>
        </DataTableScroll>
      </ActivitiesSection>
    </>
  );
};

export default AdminPanel;
